import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, shareReplay, takeUntil } from 'rxjs/operators';
import { AccountStoreService } from 'src/app/modules/account/store/account.store.service';
import { InstructorStoreService } from '../../client/store/instructor.store.service';
import { TabInfo } from '@core/models/tab';
import { TabUtility } from '@core/utils/tab.utility';
import { Country } from '@shared/models/country';
import { NotificationService } from '@core/services/notification.service';
import { CountryStoreService } from '@shared/store/country-store/services/country.store.service';
import { TRANSLATION_SCOPE } from '@modules/instructor/i18n/scope';
import { StorageService } from '@core/services/storage.service';
import { isPlatformBrowser } from '@angular/common';
import { Feedback } from "@modules/instructor/models/feedback";
import { InstructorApiService } from "@modules/instructor/client/services/instructor.api.service";
import { IdentityStoreService } from "@modules/identity/store/services/identity-store.service";
import { isEmpty, isNull, isUndefined } from "lodash";
import { InstructorStatusType } from '@modules/shared/enums/instructor-status-type';
import { Instructor } from '@modules/instructor/models/instructor';
import { instructorFeatureKey as instructors } from '@modules/instructor/client/store/instructor.state';
import { Account } from '@modules/account/models/account';

@Component({
  selector: 'app-instructor-request',
  templateUrl: './instructor-request.component.html',
  styleUrls: ['./instructor-request.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InstructorRequestComponent implements OnInit, OnDestroy {
  private destroy$: Subject<any>;
  tabs: Array<TabInfo> = [];
  tabUtility: TabUtility;

  account: Account;
  instructorFeedbacks: Feedback[] = null;
  country$: Observable<Country[]>;
  translationScope = TRANSLATION_SCOPE;

  isApplicationReadOnly: boolean = false;
  instructorStatus: InstructorStatusType = null;

  constructor(
    private notificationService: NotificationService,
    private instructorStoreService: InstructorStoreService,
    private instructorApiService: InstructorApiService,
    private accountStoreService: AccountStoreService,
    private countryStoreService: CountryStoreService,
    private identityStoreService: IdentityStoreService,
    private storage: StorageService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.destroy$ = new Subject();
    this.createInstructorRequestTab();
  }

  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
   if(this.isBrowser) {
    window.scroll({
      top: 70,
      left: 100,
      behavior: 'smooth',
    });
   }

   this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.country$ = this.countryStoreService.getAllCountries();
    if(this.isBrowser) {
      this.identityStoreService.getUserProfile().pipe(
        filter((userProfile) => !(isEmpty(userProfile) || isNull(userProfile) || isUndefined(userProfile))),
        takeUntil(this.destroy$)).subscribe((user) => {
          this.instructorStoreService.fetchInstructorProfile();
         this.instructorStoreService.fetchCourseTemplate();
      });
    }

    this.instructorStoreService
      .getInstructorProfile()
      .pipe(shareReplay(1), takeUntil(this.destroy$))
      .subscribe((instructor) => {
        if (instructor) {
          this.isApplicationReadOnly = !isNull(instructor.approved) || !isNull(instructor.blocked) || !isNull(instructor.rejected);
          this.setInstructorStatus(instructor);
          const isDraft = instructor.isDraft;
          if(!isDraft)
          this.tabUtility.CompleteTab('basic-info');

          // Verify that guide provides summary and description
          if (instructor.organizationNo && instructor.description && !isDraft) {
            this.tabUtility.ApproveTab('basic-info');
          }

          if (instructor.acceptTnC) {
            this.tabUtility.ApproveTab('my-courses');
          }

          this.instructorApiService.getInstructorFeedbacksById(instructor.id)
            .subscribe((feedbacks) => {
              this.instructorFeedbacks = feedbacks;
            });

          this.accountStoreService
            .getBusinessAccount()
            .pipe(takeUntil(this.destroy$))
            .subscribe((account) => {
              if (!account) {
                this.accountStoreService.fetchCurrentAccount();
              }
            });
        }
      });

    this.accountStoreService
      .getBusinessAccount()
      .pipe(takeUntil(this.destroy$))
      .subscribe((account) => {
        this.account = account;
        if (account) {
          if (account.status === 'VERIFIED') {
            this.tabUtility.ApproveTab('bank-account');
          } else {
            this.tabUtility.PendingTab('bank-account');
          }

          this.accountStoreService.fetchKycDocument(account.id);
        }
      });

    this.accountStoreService
      .getKycDocument()
      .pipe(takeUntil(this.destroy$))
      .subscribe((documents) => {
        if (documents.length) {
          const isPending = documents.find((doc) => doc.status === 'PENDING');
          if (!isPending) {
            this.tabUtility.ApproveTab('id-verification');
          } else {
            this.tabUtility.PendingTab('id-verification');
            if(this.account) {
              setTimeout(() => this.accountStoreService.fetchKycDocument(this.account.id), 5000);
            }
          }
        }
      });
  }

  setInstructorStatus(instructor: Instructor): void {
    if(!isNull(instructor.rejected)) {
      this.instructorStatus = InstructorStatusType.REJECTED;
    }
    else if(!isNull(instructor.blocked)) {
      this.instructorStatus = InstructorStatusType.BLOCKED;
    }
    else if(!isNull(instructor.returned)) {
      this.instructorStatus = InstructorStatusType.RETURNED;
    }
    else if(!isNull(instructor.approved)) {
      this.instructorStatus = InstructorStatusType.APPROVED;
    }
    else {
      this.instructorStatus = InstructorStatusType.NOT_YET_APPROVED;
    }
  }

  showValidationMessage(isComplete: boolean): boolean {
    if (!isComplete) {
      this.notificationService.warning('finish_basic_info_prompt', instructors);
    }

    return isComplete;
  }

  isSetTopMargin(): boolean {
    const value = this.storage.getItem('apps');
    return value && value === 'app-instructor-onboarding';
  }

  private createInstructorRequestTab(): void {
    this.tabs = [
      {
        isActive: false,
        isComplete: false,
        title: 'basic_information',
        name: 'basic-info',
        isPending: false,
        isApprove: false,
        isRejected: false,
      },
      {
        isActive: false,
        isComplete: false,
        title: 'id_verification',
        name: 'id-verification',
        isPending: false,
        isApprove: false,
        isRejected: false,
      },
      {
        isActive: false,
        isComplete: false,
        title: 'bank_account',
        name: 'bank-account',
        isPending: false,
        isApprove: false,
        isRejected: false,
      },
      {
        isActive: false,
        isComplete: false,
        title: 'my_courses',
        name: 'my-courses',
        isPending: false,
        isApprove: false,
        isRejected: false,
      },
      {
        isActive: false,
        isComplete: false,
        title: 'my_feedbacks',
        name: 'my-feedbacks',
        isPending: false,
        isApprove: false,
        isRejected: false,
      }
    ];

    this.tabUtility = new TabUtility(this.tabs);
    this.tabUtility.ActiveTab('basic-info');
  }

  changeTab(tab: TabInfo): void {
    if(this.showValidationMessage(this.tabUtility.IsTabComplete('basic-info'))) {
      if(this.isBrowser) {
        window.scroll({
          top: 70,
          left: 100,
          behavior: 'smooth',
        });
      }
      this.tabUtility.ActiveTab(tab.name);
    }
  }
}
